import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  CalendarBlankIcon,
  CoinIcon,
  ScalesIcon,
  ShieldCheckIcon,
  StorefrontIcon,
  WarningIcon,
} from '@phosphor-icons/react/dist/ssr';

import { AuthLoadingScreen } from '@/components/templates';
import { ScreenShell } from '@/components/screen-shell';
import { NavBar } from '@/components/nav-bar';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardEyebrow,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { KeyValue } from '@/components/ui/key-value';

import {
  createOrderDraft,
  getMarketDetail,
  getWalletStatus,
  type MarketDetail,
  type MarketOutcomeSummary,
} from '../api';
import { useAuth } from '../auth';
import { useT, type TFunction } from '../i18n';
import { useTelegramBackButton } from '../lib/telegram-back-button';
import { useNavStackEntry } from '../lib/nav-stack';
import { tryGetTelegram } from '../telegram';

const QUICK_BUY_USD = [1, 5, 25];
const MIN_ORDER_SHARES = 5;

function fmtPriceCents(p: number): string {
  return `${Math.round(p * 100)}\u00a2`;
}

function fmtUsd(n: number | null, t: TFunction): string {
  if (n === null || !Number.isFinite(n) || n <= 0)
    return t('market.list.no_volume');
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
  return `$${n.toFixed(0)}`;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function minUsdForPrice(price: number): number {
  if (!Number.isFinite(price) || price <= 0) return Infinity;
  return MIN_ORDER_SHARES * price;
}

type DraftError =
  | { kind: 'generic'; message: string }
  | { kind: 'approvals' }
  | {
      kind: 'insufficient';
      have: number;
      need: number;
      slug: string;
      outcome: string;
      amount: number;
    };

/**
 * The backend rejects orders from a deposit wallet that hasn't run the
 * one-time "Enable trading" setup with `"400 Deposit wallet has no allowance
 * for Polymarket CTF Exchange V2…"` (code `NEEDS_APPROVALS`). We detect it so
 * we can route the user to `/approvals` instead of dead-ending on a raw error.
 */
function isApprovalsError(raw: string): boolean {
  return /needs_approvals|allowance for polymarket|enable trading/i.test(raw);
}

/**
 * The backend surfaces insufficient-balance as `"400 Insufficient pUSD balance:
 * have $0.00, need $4.99. Deposit pUSD to your wallet and try again."`. We
 * parse it so we can render a deep-linked Deposit CTA instead of dead-ending
 * the user on a plain error.
 */
function parseInsufficientBalance(
  raw: string,
): { have: number; need: number } | null {
  const m = raw.match(
    /insufficient\s+pusd\s+balance.*?\$(\d+(?:\.\d+)?).*?\$(\d+(?:\.\d+)?)/i,
  );
  if (!m) return null;
  const have = Number(m[1]);
  const need = Number(m[2]);
  if (!Number.isFinite(have) || !Number.isFinite(need)) return null;
  return { have, need };
}

export default function MarketDetail() {
  const t = useT();
  const navigate = useNavigate();
  const auth = useAuth();
  const params = useParams<{ slug: string }>();
  const slug = params.slug ?? null;

  const [market, setMarket] = useState<MarketDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [drafting, setDrafting] = useState<string | null>(null);
  const [draftError, setDraftError] = useState<DraftError | null>(null);
  // null = unknown (status not loaded / failed), true/false = on-chain result.
  const [approvalsReady, setApprovalsReady] = useState<boolean | null>(null);

  const breadcrumbLabel = useMemo(
    () => market?.question ?? t('market.detail.breadcrumb'),
    [market?.question, t],
  );

  useTelegramBackButton();
  useNavStackEntry({ label: breadcrumbLabel });

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await getMarketDetail(slug);
        if (!cancelled) setMarket(data);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Proactively check whether the deposit wallet has run the one-time
  // "Enable trading" setup, so we can gate the buy buttons behind it instead
  // of letting the user draft an order only to fail at submit.
  const externalUserId =
    auth.state === 'ready' ? auth.auth.externalUserId : null;
  useEffect(() => {
    if (!externalUserId) return;
    let cancelled = false;
    (async () => {
      try {
        const status = await getWalletStatus(externalUserId);
        if (!cancelled) setApprovalsReady(status.hasApprovals);
      } catch {
        // Best-effort: if the status read fails, leave the buttons enabled and
        // let onBuy surface the (reactive) approvals error instead of blocking.
        if (!cancelled) setApprovalsReady(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [externalUserId]);

  async function onBuy(outcome: MarketOutcomeSummary, amount: number) {
    if (!market) return;
    if (auth.state !== 'ready') {
      setDraftError({ kind: 'generic', message: t('market.detail.auth_required') });
      tryGetTelegram()?.HapticFeedback?.notificationOccurred?.('error');
      return;
    }
    const key = `${outcome.tokenId}:${amount}`;
    setDrafting(key);
    setDraftError(null);
    try {
      const draft = await createOrderDraft({
        marketSlug: market.slug,
        outcomeLabel: outcome.label,
        side: 'BUY',
        amount,
      });
      tryGetTelegram()?.HapticFeedback?.notificationOccurred?.('success');
      const qs = new URLSearchParams({
        orderId: draft.orderId,
        slug: market.slug,
        outcome: outcome.label,
        side: 'BUY',
        amount: String(amount),
      });
      navigate(`/sign-order?${qs.toString()}`);
    } catch (err) {
      tryGetTelegram()?.HapticFeedback?.notificationOccurred?.('error');
      const raw = (err as Error).message;
      const parsed = parseInsufficientBalance(raw);
      if (parsed) {
        setDraftError({
          kind: 'insufficient',
          have: parsed.have,
          need: parsed.need,
          slug: market.slug,
          outcome: outcome.label,
          amount,
        });
      } else if (isApprovalsError(raw)) {
        setApprovalsReady(false);
        setDraftError({ kind: 'approvals' });
      } else {
        setDraftError({ kind: 'generic', message: raw });
      }
    } finally {
      setDrafting(null);
    }
  }

  if (!slug) {
    return (
      <ScreenShell
        eyebrow={
          <span className="inline-flex items-center gap-1.5">
            <StorefrontIcon weight="fill" className="size-3" />
            {t('market.detail.eyebrow')}
          </span>
        }
        title={t('market.detail.error_title')}
      >
        <NavBar />
        <Alert tone="warning">
          <WarningIcon weight="fill" className="size-4" />
          <AlertTitle>{t('market.detail.error_title')}</AlertTitle>
          <AlertDescription>
            <Link to="/markets" className="underline">
              {t('market.list.cta_categories')}
            </Link>
          </AlertDescription>
        </Alert>
      </ScreenShell>
    );
  }

  if (loading && !market) {
    return (
      <AuthLoadingScreen
        title={t('market.detail.loading_title')}
        description={t('market.detail.loading_desc')}
        shortDescription={t('market.detail.loading_short')}
        steps={[
          { label: t('market.detail.eyebrow'), color: 'bg-primary' },
          { label: 'Polymarket', color: 'bg-sky-500', delay: '0.4s' },
        ]}
      />
    );
  }

  if (error || !market) {
    return (
      <ScreenShell
        eyebrow={
          <span className="inline-flex items-center gap-1.5">
            <StorefrontIcon weight="fill" className="size-3" />
            {t('market.detail.eyebrow')}
          </span>
        }
        title={t('market.detail.error_title')}
      >
        <NavBar />
        <Alert tone="danger">
          <WarningIcon weight="fill" className="size-4" />
          <AlertTitle>{t('market.detail.error_title')}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </ScreenShell>
    );
  }

  const spread =
    market.bestBid !== null && market.bestAsk !== null
      ? Math.max(0, market.bestAsk - market.bestBid)
      : null;

  return (
    <ScreenShell
      eyebrow={
        <span className="inline-flex items-center gap-1.5">
          <StorefrontIcon weight="fill" className="size-3" />
          {market.category ?? t('market.detail.eyebrow')}
        </span>
      }
      title={market.question}
    >
      <NavBar />

      <Card>
        <CardHeader>
          <div className="flex items-start gap-3">
            {market.icon ?? market.image ? (
              <img
                src={(market.icon ?? market.image) as string}
                alt=""
                loading="lazy"
                className="size-14 shrink-0 rounded-2xl object-cover ring-1 ring-foreground/10"
              />
            ) : (
              <span className="bg-primary/12 ring-primary/25 inline-flex size-14 shrink-0 items-center justify-center rounded-2xl ring-1">
                <StorefrontIcon weight="fill" className="text-primary size-7" />
              </span>
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <CardTitle className="text-base sm:text-lg">
                {market.question}
              </CardTitle>
              <CardDescription>
                <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]">
                  <span className="inline-flex items-center gap-1 tabular-nums">
                    <CoinIcon weight="fill" className="size-3" />
                    {t('market.detail.volume24h_label')}:{' '}
                    {fmtUsd(market.volume24hr, t)}
                  </span>
                  {market.endDate ? (
                    <span className="inline-flex items-center gap-1 tabular-nums">
                      <CalendarBlankIcon weight="fill" className="size-3" />
                      {t('market.detail.ends_label')}: {fmtDate(market.endDate)}
                    </span>
                  ) : null}
                </span>
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="grid grid-cols-3 gap-2">
          <KeyValue
            label={t('market.detail.volume_total_label')}
            value={fmtUsd(market.volume, t)}
            mono
          />
          <KeyValue
            label={t('market.detail.liquidity_label')}
            value={fmtUsd(market.liquidity, t)}
            mono
          />
          <KeyValue
            label={t('market.detail.spread_label')}
            value={spread === null ? '—' : fmtPriceCents(spread)}
            mono
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardEyebrow>
            <ScalesIcon weight="fill" className="size-3" />
            {t('market.detail.outcomes_title')}
          </CardEyebrow>
          <CardDescription>
            {t('market.detail.outcomes_description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {approvalsReady === false ? (
            <div className="border-primary/30 bg-primary/8 mb-3 flex flex-col gap-3 rounded-2xl border p-4">
              <div className="flex items-center gap-2">
                <span className="bg-primary/15 ring-primary/25 inline-flex size-8 shrink-0 items-center justify-center rounded-xl ring-1">
                  <ShieldCheckIcon weight="fill" className="text-primary size-4" />
                </span>
                <span className="text-foreground text-[14px] font-semibold tracking-tight">
                  {t('market.detail.approvals_title')}
                </span>
              </div>
              <p className="text-muted-foreground text-[12px] leading-relaxed">
                {t('market.detail.approvals_body')}
              </p>
              <Link
                to="/approvals"
                className={buttonVariants({ size: 'lg' })}
              >
                <ShieldCheckIcon weight="bold" className="size-4" />
                {t('market.detail.approvals_cta')}
              </Link>
            </div>
          ) : null}
          {market.outcomes.map((o) => {
            const minUsd = minUsdForPrice(o.price);
            return (
              <div
                key={o.tokenId}
                className="bg-card/70 ring-foreground/10 flex flex-col gap-3 rounded-2xl p-3 ring-1"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-foreground text-[15px] font-semibold tracking-tight">
                    {o.label}
                  </span>
                  <span className="text-primary tabular-nums text-base font-semibold">
                    {fmtPriceCents(o.price)}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {QUICK_BUY_USD.map((amt) => {
                    const disabled =
                      amt < minUsd ||
                      drafting !== null ||
                      approvalsReady === false;
                    const key = `${o.tokenId}:${amt}`;
                    const isBusy = drafting === key;
                    return (
                      <Button
                        key={amt}
                        type="button"
                        size="sm"
                        variant={amt === 5 ? 'default' : 'outline'}
                        disabled={disabled}
                        onClick={() => onBuy(o, amt)}
                        className="justify-center"
                      >
                        {isBusy
                          ? t('market.detail.drafting')
                          : `$${amt}`}
                      </Button>
                    );
                  })}
                </div>
                {Number.isFinite(minUsd) && minUsd > QUICK_BUY_USD[0] ? (
                  <p className="text-muted-foreground text-[11px] tabular-nums">
                    {t('market.detail.min_order', {
                      amount: minUsd.toFixed(2),
                    })}
                  </p>
                ) : null}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {draftError ? (
        draftError.kind === 'insufficient' ? (
          <Alert tone="danger">
            <WarningIcon weight="fill" className="size-4" />
            <AlertTitle>{t('market.detail.no_balance_title')}</AlertTitle>
            <AlertDescription className="flex flex-col gap-3">
              <span>
                {t('market.detail.no_balance_body', {
                  have: `$${draftError.have.toFixed(2)}`,
                  need: `$${draftError.need.toFixed(2)}`,
                })}
              </span>
              <Button
                size="sm"
                onClick={() => {
                  const qs = new URLSearchParams({
                    intent: 'buy',
                    slug: draftError.slug,
                    outcome: draftError.outcome,
                    usd: String(draftError.amount),
                    required: String(draftError.need),
                  });
                  navigate(`/deposit?${qs.toString()}`);
                }}
              >
                {t('market.detail.deposit_cta')}
              </Button>
            </AlertDescription>
          </Alert>
        ) : draftError.kind === 'approvals' ? (
          <Alert tone="warning">
            <ShieldCheckIcon weight="fill" className="size-4" />
            <AlertTitle>{t('market.detail.approvals_title')}</AlertTitle>
            <AlertDescription className="flex flex-col gap-3">
              <span>{t('market.detail.approvals_body')}</span>
              <Link
                to="/approvals"
                className={buttonVariants({ size: 'sm' })}
              >
                <ShieldCheckIcon weight="bold" className="size-4" />
                {t('market.detail.approvals_cta')}
              </Link>
            </AlertDescription>
          </Alert>
        ) : (
          <Alert tone="danger">
            <WarningIcon weight="fill" className="size-4" />
            <AlertTitle>{t('market.detail.draft_failed_title')}</AlertTitle>
            <AlertDescription>{draftError.message}</AlertDescription>
          </Alert>
        )
      ) : null}

      <Card>
        <CardHeader>
          <CardEyebrow>
            <ScalesIcon weight="fill" className="size-3" />
            {t('market.detail.preview_title')}
          </CardEyebrow>
          <CardDescription>
            {t('market.detail.preview_description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-2">
          <KeyValue
            label={t('market.detail.bestbid_label')}
            value={
              market.bestBid === null ? '—' : fmtPriceCents(market.bestBid)
            }
            mono
          />
          <KeyValue
            label={t('market.detail.bestask_label')}
            value={
              market.bestAsk === null ? '—' : fmtPriceCents(market.bestAsk)
            }
            mono
          />
        </CardContent>
      </Card>

      {market.description ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t('market.detail.about_title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground text-[13px] leading-relaxed whitespace-pre-line">
              {market.description}
            </p>
          </CardContent>
        </Card>
      ) : null}
    </ScreenShell>
  );
}
